/**
 * AchievementManagementPage
 * 
 * Admin page for managing user achievements, points, and gamification data.
 * Features:
 * - View all users and their gamification stats
 * - Manually adjust points
 * - Grant/revoke achievements to users
 * - Reset yearly data
 * - Create/edit/enable/disable achievements (unified system)
 * - Edit point values for different activities (scoring system)
 * - Customize level thresholds
 * - Generate test data for demonstration
 * - Trim activity log history
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../components/auth/AuthContext';
import { canViewAllData, FEATURE_PERMISSIONS, hasPermission } from '../utils/permissions';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import GamificationMaintenancePage from './GamificationMaintenancePage';
import {
  getBlockedActivityLog,
  markBlockedActivityReviewed,
  markBlockedActivityAwarded,
  getBlockedActivitySummary,
  bulkDismissBlockedActivities,
  bulkAwardBlockedActivities
} from '../utils/gamificationAntiAbuse';
import {
  LEVELS,
  getAllGamificationProfiles,
  adminAdjustPoints,
  adminAdjustStreak,
  adminAdjustLifetimePoints,
  adminGrantMultipleAchievements,
  adminRevokeAchievement,
  adminResetYearlyData,
  getPointsLog,
  getUserLevel,
  getCurrentYear,
  generateTestGamificationData,
  clearGamificationData,
  getAchievementsFromFirestore,
  createAchievement,
  updateAchievement,
  deleteAchievement,
  seedAchievementsToFirestore,
  trimActivityLog,
  getLevelOverrides,
  updateLevelOverride,
  resetLevelOverride,
  getAllPointValues,
  updatePointValue,
  resetPointValue,
  createPointValue,
  deletePointValue,
  togglePointValueEnabled,
  DEFAULT_POINT_VALUES,
  DEFAULT_ACHIEVEMENTS
} from '../utils/gamificationUtils';
import { exportToCSV } from '../utils/exportUtils';
import { logUserAction } from '../utils/logger';
import Tooltip from '../components/common/Tooltip';
import Toast from '../components/common/Toast';

const AchievementManagementPage = () => {
  const { currentUser } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [pointsLog, setPointsLog] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [levelOverrides, setLevelOverrides] = useState({});
  const [pointValues, setPointValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [achievementFilter, setAchievementFilter] = useState('all');
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  
  // Modals
  const [adjustPointsModal, setAdjustPointsModal] = useState({ show: false, user: null });
  const [grantAchievementModal, setGrantAchievementModal] = useState({ show: false, user: null });
  const [confirmReset, setConfirmReset] = useState({ show: false, user: null });
  const [testDataModal, setTestDataModal] = useState({ show: false, user: null });
  const [achievementModal, setAchievementModal] = useState({ show: false, achievement: null });
  const [trimLogModal, setTrimLogModal] = useState({ show: false });
  const [levelModal, setLevelModal] = useState({ show: false, level: null });
  const [pointValueModal, setPointValueModal] = useState({ show: false, pointValue: null });
  const [achievementsSeeded, setAchievementsSeeded] = useState(false);
  const [seeding, setSeeding] = useState(false);
  
  // Activity Audit state
  const [blockedActivityLog, setBlockedActivityLog] = useState([]);
  const [blockedActivitySummary, setBlockedActivitySummary] = useState(null);
  const [auditFilter, setAuditFilter] = useState('pending');
  const [auditLoading, setAuditLoading] = useState(false);
  const [selectedAuditLogs, setSelectedAuditLogs] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // User Stats adjustment modals
  const [adjustStreakModal, setAdjustStreakModal] = useState({ show: false, user: null });
  const [adjustLifetimeModal, setAdjustLifetimeModal] = useState({ show: false, user: null });
  
  // Activity Log state (sorting and filtering)
  const [logSortField, setLogSortField] = useState('timestamp');
  const [logSortDir, setLogSortDir] = useState('desc');
  const [logUserFilter, setLogUserFilter] = useState('');
  const [logActionFilter, setLogActionFilter] = useState('');
  
  const availableYears = useMemo(() => {
    const currentYear = getCurrentYear();
    return [currentYear, currentYear - 1, currentYear - 2];
  }, []);
  
  // Filter achievements based on filter selection
  const filteredAchievements = useMemo(() => {
    if (achievementFilter === 'all') return achievements;
    if (achievementFilter === 'automatic') return achievements.filter(a => a.isAutomatic);
    if (achievementFilter === 'manual') return achievements.filter(a => !a.isAutomatic);
    if (achievementFilter === 'disabled') return achievements.filter(a => a.isEnabled === false);
    return achievements;
  }, [achievements, achievementFilter]);
  
  // Group achievements by category
  const achievementsByCategory = useMemo(() => {
    const grouped = {};
    for (const ach of filteredAchievements) {
      const cat = ach.category || 'other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(ach);
    }
    return grouped;
  }, [filteredAchievements]);
  
  const mergedLevels = useMemo(() => {
    return LEVELS.map(level => ({
      ...level,
      ...(levelOverrides[level.level] || {}),
      hasOverride: !!levelOverrides[level.level]
    }));
  }, [levelOverrides]);
  
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [profilesData, usersSnap, logData, achData, lvlOverrides, ptValues, seededSnap] = await Promise.all([
          getAllGamificationProfiles(),
          getDocs(collection(db, collectionPath('users'))),
          getPointsLog(null, 200),
          getAchievementsFromFirestore().catch(() => []),
          getLevelOverrides().catch(() => ({})),
          getAllPointValues().catch(() => []),
          getDoc(doc(db, collectionPath('config'), 'achievementsSeeded')).catch(() => null)
        ]);
        
        setProfiles(profilesData);
        setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.role !== 'disabled'));
        setPointsLog(logData);
        setAchievements(achData);
        setLevelOverrides(lvlOverrides);
        setPointValues(ptValues);
        setAchievementsSeeded(seededSnap?.exists?.() || false);
      } catch (error) {
        console.error('Failed to load data:', error);
        setToast({ message: 'Failed to load data', type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);
  
  const usersById = useMemo(() => new Map(users.map(u => [u.id, u])), [users]);

  // Set of user IDs hidden from reports/achievements
  const hiddenUserIds = useMemo(() => 
    new Set(users.filter(u => u.hideFromReports).map(u => u.id)),
    [users]
  );
  
  const filteredProfiles = useMemo(() => {
    let result = profiles.filter(p => !hiddenUserIds.has(p.userId));
    if (!search.trim()) return result;
    const q = search.toLowerCase();
    return result.filter(p => {
      const user = usersById.get(p.userId);
      const name = (user?.displayName || '').toLowerCase();
      const email = (user?.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [profiles, search, usersById, hiddenUserIds]);
  
  const sortedProfiles = useMemo(() => {
    return [...filteredProfiles].sort((a, b) => {
      const aPoints = a.yearlyPoints?.[selectedYear] || 0;
      const bPoints = b.yearlyPoints?.[selectedYear] || 0;
      return bPoints - aPoints;
    });
  }, [filteredProfiles, selectedYear]);
  
  // Activity Log: Get unique actions for filter dropdown
  const uniqueLogActions = useMemo(() => {
    const actions = new Set(pointsLog.map(l => l.action));
    return Array.from(actions).sort();
  }, [pointsLog]);
  
  // Activity Log: Filter and sort
  const filteredAndSortedLog = useMemo(() => {
    let logs = [...pointsLog];
    
    // Filter by user
    if (logUserFilter) {
      logs = logs.filter(l => l.userId === logUserFilter);
    }
    
    // Filter by action
    if (logActionFilter) {
      logs = logs.filter(l => l.action === logActionFilter);
    }
    
    // Sort
    logs.sort((a, b) => {
      let aVal, bVal;
      
      switch (logSortField) {
        case 'timestamp':
          aVal = a.timestamp?.toDate?.() || new Date(0);
          bVal = b.timestamp?.toDate?.() || new Date(0);
          return logSortDir === 'asc' ? aVal - bVal : bVal - aVal;
        case 'user':
          aVal = (usersById.get(a.userId)?.displayName || '').toLowerCase();
          bVal = (usersById.get(b.userId)?.displayName || '').toLowerCase();
          return logSortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        case 'action':
          return logSortDir === 'asc' ? a.action.localeCompare(b.action) : b.action.localeCompare(a.action);
        case 'points':
          return logSortDir === 'asc' ? a.points - b.points : b.points - a.points;
        default:
          return 0;
      }
    });
    
    return logs;
  }, [pointsLog, logUserFilter, logActionFilter, logSortField, logSortDir, usersById]);
  
  // Activity Log: Toggle sort
  const toggleLogSort = (field) => {
    if (logSortField === field) {
      setLogSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setLogSortField(field);
      setLogSortDir('desc');
    }
  };
  
  // Load blocked activity log when Audit tab is selected
  const loadAuditData = useCallback(async () => {
    setAuditLoading(true);
    try {
      // Build filter options based on auditFilter
      const filterOptions = { limitCount: 100 };
      if (auditFilter === 'pending') {
        filterOptions.reviewed = false;
        filterOptions.awarded = false;
      } else if (auditFilter === 'reviewed') {
        filterOptions.reviewed = true;
        filterOptions.awarded = false;
      } else if (auditFilter === 'awarded') {
        filterOptions.awarded = true;
      }
      // 'all' doesn't set any filters
      
      const [logData, summary] = await Promise.all([
        getBlockedActivityLog(filterOptions),
        getBlockedActivitySummary()
      ]);
      setBlockedActivityLog(logData);
      setBlockedActivitySummary(summary);
    } catch (error) {
      console.error('Failed to load audit data:', error);
      setToast({ message: 'Failed to load audit data', type: 'error' });
    } finally {
      setAuditLoading(false);
    }
  }, [auditFilter]);
  
  useEffect(() => {
    if (activeTab === 'audit') {
      loadAuditData();
    }
  }, [activeTab, auditFilter, loadAuditData]);
  
  const handleMarkReviewed = async (logId) => {
    try {
      await markBlockedActivityReviewed(currentUser?.uid, logId, 'Reviewed by admin');
      setToast({ message: 'Marked as reviewed', type: 'success' });
      loadAuditData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };
  
  const handleAwardPoints = async (log) => {
    const reason = window.prompt('Enter reason for manual point award:', 'Activity verified as legitimate');
    if (!reason) return;
    try {
      await markBlockedActivityAwarded(currentUser?.uid, log.id, log.pointsWouldHaveAwarded || 0, reason);
      setToast({ message: `Awarded ${log.pointsWouldHaveAwarded || 0} points`, type: 'success' });
      loadAuditData();
      refreshData(); // Refresh points data too
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };
  
  // Bulk dismiss selected audit logs
  const handleBulkDismiss = async () => {
    if (selectedAuditLogs.size === 0) return;
    const reason = window.prompt(`Dismiss ${selectedAuditLogs.size} selected activities? Enter reason:`, 'Bulk dismissed - not legitimate');
    if (!reason) return;
    
    setBulkActionLoading(true);
    try {
      const result = await bulkDismissBlockedActivities(currentUser?.uid, Array.from(selectedAuditLogs), reason);
      setToast({ message: `Dismissed ${result.success} activities`, type: 'success' });
      setSelectedAuditLogs(new Set());
      loadAuditData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // Bulk award selected audit logs
  const handleBulkAward = async () => {
    if (selectedAuditLogs.size === 0) return;
    const selectedLogs = blockedActivityLog.filter(l => selectedAuditLogs.has(l.id));
    const totalPoints = selectedLogs.reduce((sum, l) => sum + (l.pointsWouldHaveAwarded || 0), 0);
    
    const reason = window.prompt(`Award points for ${selectedAuditLogs.size} activities (total: ${totalPoints} pts)? Enter reason:`, 'Bulk approved - activities verified as legitimate');
    if (!reason) return;
    
    setBulkActionLoading(true);
    try {
      const result = await bulkAwardBlockedActivities(currentUser?.uid, selectedLogs, reason);
      setToast({ message: `Awarded ${result.totalPointsAwarded} points across ${result.success} activities`, type: 'success' });
      setSelectedAuditLogs(new Set());
      loadAuditData();
      refreshData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // Dismiss all pending
  const handleDismissAll = async () => {
    const pendingLogs = blockedActivityLog.filter(l => !l.reviewed && !l.awarded);
    if (pendingLogs.length === 0) {
      setToast({ message: 'No pending activities to dismiss', type: 'info' });
      return;
    }
    
    const reason = window.prompt(`Dismiss ALL ${pendingLogs.length} pending activities? Enter reason:`, 'Bulk dismissed - not legitimate');
    if (!reason) return;
    
    setBulkActionLoading(true);
    try {
      const result = await bulkDismissBlockedActivities(currentUser?.uid, pendingLogs.map(l => l.id), reason);
      setToast({ message: `Dismissed ${result.success} activities`, type: 'success' });
      setSelectedAuditLogs(new Set());
      loadAuditData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // Award all pending
  const handleAwardAll = async () => {
    const pendingLogs = blockedActivityLog.filter(l => !l.reviewed && !l.awarded);
    if (pendingLogs.length === 0) {
      setToast({ message: 'No pending activities to award', type: 'info' });
      return;
    }
    
    const totalPoints = pendingLogs.reduce((sum, l) => sum + (l.pointsWouldHaveAwarded || 0), 0);
    const reason = window.prompt(`Award ALL ${pendingLogs.length} pending activities (total: ${totalPoints} pts)? Enter reason:`, 'Bulk approved - activities verified as legitimate');
    if (!reason) return;
    
    setBulkActionLoading(true);
    try {
      const result = await bulkAwardBlockedActivities(currentUser?.uid, pendingLogs, reason);
      setToast({ message: `Awarded ${result.totalPointsAwarded} points across ${result.success} activities`, type: 'success' });
      setSelectedAuditLogs(new Set());
      loadAuditData();
      refreshData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // Toggle audit log selection
  const toggleAuditLogSelection = (logId) => {
    setSelectedAuditLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) newSet.delete(logId);
      else newSet.add(logId);
      return newSet;
    });
  };
  
  // Select/deselect all pending audit logs
  const toggleSelectAllAuditLogs = () => {
    const pendingLogs = blockedActivityLog.filter(l => !l.reviewed && !l.awarded);
    if (selectedAuditLogs.size === pendingLogs.length) {
      setSelectedAuditLogs(new Set());
    } else {
      setSelectedAuditLogs(new Set(pendingLogs.map(l => l.id)));
    }
  };
  
  // Export handlers
  const handleExportActivityAudit = () => {
    const headers = ['Timestamp', 'User', 'Action', 'Block Reason', 'Points Would Award', 'Status', 'Reviewed By', 'Admin Notes'];
    // Build filename with filter info
    const filterLabel = auditFilter === 'all' ? 'all' : auditFilter;
    const filename = `activity_audit_${filterLabel}_${new Date().toISOString().split('T')[0]}`;
    
    exportToCSV(
      filename,
      headers,
      blockedActivityLog,
      (log) => {
        const user = usersById.get(log.userId);
        const timestamp = log.timestamp?.toDate?.() || new Date();
        return [
          timestamp.toISOString(),
          user?.displayName || 'Unknown',
          log.action || '',
          log.blockReason || '',
          log.pointsWouldHaveAwarded || 0,
          log.awarded ? 'Awarded' : log.reviewed ? 'Dismissed' : 'Pending',
          log.reviewedBy ? (usersById.get(log.reviewedBy)?.displayName || log.reviewedBy) : '',
          log.adminNotes || ''
        ];
      }
    );
    // Audit log the export (non-blocking)
    logUserAction({
      action: 'Data Exported',
      details: `Exported ${blockedActivityLog.length} records as CSV: ${filename} — filter: ${filterLabel}`,
      entityType: 'ActivityAudit',
      entityId: filename,
    }).catch(err => console.warn('Failed to log export:', err));
    setToast({ message: `Audit log exported (${filterLabel})`, type: 'success' });
  };
  
  const handleExportActivityLog = () => {
    const headers = ['Timestamp', 'User', 'Action', 'Points', 'Details'];
    // Build filename with filter info
    const filterParts = [];
    if (logUserFilter) {
      const userName = usersById.get(logUserFilter)?.displayName || 'user';
      filterParts.push(userName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase());
    }
    if (logActionFilter) {
      filterParts.push(logActionFilter.toLowerCase());
    }
    const filterLabel = filterParts.length > 0 ? filterParts.join('_') : 'all';
    const filename = `activity_log_${filterLabel}_${new Date().toISOString().split('T')[0]}`;
    
    exportToCSV(
      filename,
      headers,
      filteredAndSortedLog,
      (log) => {
        const user = usersById.get(log.userId);
        const timestamp = log.timestamp?.toDate?.() || new Date();
        const detailParts = [];
        if (log.metadata?.reason) detailParts.push(log.metadata.reason);
        if (log.metadata?.achievementName) detailParts.push(`Achievement: ${log.metadata.achievementName}`);
        if (log.metadata?.entityId) detailParts.push(`Entity: ${log.metadata.entityId}`);
        if (log.metadata?.dealValue) detailParts.push(`Deal: $${log.metadata.dealValue}`);
        return [
          timestamp.toISOString(),
          user?.displayName || (log.userId === 'SYSTEM' ? 'System' : 'Unknown'),
          log.action || '',
          log.points || 0,
          detailParts.join(' | ')
        ];
      }
    );
    // Audit log the export (non-blocking)
    logUserAction({
      action: 'Data Exported',
      details: `Exported ${filteredAndSortedLog.length} records as CSV: ${filename} — filter: ${filterLabel}`,
      entityType: 'ActivityLog',
      entityId: filename,
    }).catch(err => console.warn('Failed to log export:', err));
    setToast({ message: `Activity log exported (${filterLabel})`, type: 'success' });
  };
  
  const handleExportUserStats = () => {
    const headers = ['User', 'Email', 'Level', `${selectedYear} Points`, 'Lifetime Points', `Achievements (${selectedYear})`, 'Current Streak', 'Longest Streak'];
    // Build filename with year and search filter
    const filterParts = [selectedYear.toString()];
    if (search.trim()) {
      filterParts.push(search.trim().replace(/[^a-zA-Z0-9]/g, '_').toLowerCase());
    }
    const filename = `user_stats_${filterParts.join('_')}_${new Date().toISOString().split('T')[0]}`;
    
    exportToCSV(
      filename,
      headers,
      sortedProfiles,
      (profile) => {
        const user = usersById.get(profile.userId);
        const yearPoints = profile.yearlyPoints?.[selectedYear] || 0;
        const yearAchievements = profile.achievementsByYear?.[selectedYear]?.length || 0;
        const level = getUserLevel(yearPoints);
        return [
          user?.displayName || 'Unknown User',
          user?.email || '',
          `${level.icon} ${level.name}`,
          yearPoints,
          profile.totalPoints || 0,
          yearAchievements,
          profile.currentStreak || 0,
          profile.longestStreak || 0
        ];
      }
    );
    const filterDesc = search.trim() ? `${selectedYear}, filtered` : selectedYear;
    // Audit log the export (non-blocking)
    logUserAction({
      action: 'Data Exported',
      details: `Exported ${sortedProfiles.length} records as CSV: ${filename} — ${filterDesc}`,
      entityType: 'UserStats',
      entityId: filename,
    }).catch(err => console.warn('Failed to log export:', err));
    setToast({ message: `User stats exported (${filterDesc})`, type: 'success' });
  };
  
  const refreshData = async () => {
    try {
      const [profilesData, logData, achData, lvlOverrides, ptValues] = await Promise.all([
        getAllGamificationProfiles(),
        getPointsLog(null, 200),
        getAchievementsFromFirestore().catch(() => []),
        getLevelOverrides().catch(() => ({})),
        getAllPointValues().catch(() => [])
      ]);
      setProfiles(profilesData);
      setPointsLog(logData);
      setAchievements(achData);
      setLevelOverrides(lvlOverrides);
      setPointValues(ptValues);
    } catch (error) {
      console.error('Failed to refresh data:', error);
    }
  };
  
  // Seed achievements to Firestore (one-time migration)
  const handleSeedAchievements = async () => {
    if (!window.confirm('This will seed all default achievements to Firestore. Continue?')) return;
    setSeeding(true);
    try {
      const result = await seedAchievementsToFirestore(currentUser?.uid);
      setToast({ message: result.message, type: 'success' });
      setAchievementsSeeded(true);
      refreshData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    } finally {
      setSeeding(false);
    }
  };
  
  const isReadOnly = !hasPermission(currentUser, FEATURE_PERMISSIONS.ACHIEVEMENTS_MANAGE);
  
  if (!currentUser || !canViewAllData(currentUser)) {
    return (
      <main className="flex-grow p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-4xl mb-2">🔒</div>
          <h2 className="text-xl font-bold text-red-700">Access Denied</h2>
          <p className="text-red-600">Only administrators can access this page.</p>
        </div>
      </main>
    );
  }
  
  return (
    <main className="flex-grow p-6 overflow-y-auto">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            🏆 Achievement Management
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage user achievements, points, and gamification data
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">Year:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="flex gap-2 mb-6 border-b overflow-x-auto">
        {[
          { id: 'users', label: 'User Stats', icon: '👥' },
          { id: 'achievements', label: 'Achievements', icon: '🏅' },
          { id: 'levels', label: 'Levels', icon: '📊' },
          { id: 'scoring', label: 'Scoring', icon: '⚡' },
          { id: 'log', label: 'Activity Log', icon: '📋' },
          { id: 'audit', label: 'Activity Audit', icon: '🛡️' },
          { id: 'maintenance', label: 'Maintenance', icon: '🛠️' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>
      
      {activeTab === 'users' && (
        <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`w-full md:w-96 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
              search ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
            }`}
          />
          <button onClick={handleExportUserStats} className="px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-sm font-medium whitespace-nowrap">
            📥 Export CSV
          </button>
        </div>
      )}
      
      {loading ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">Loading...</div>
      ) : (
        <>
          {activeTab === 'users' && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Level</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">{selectedYear} Points</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Lifetime Points</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Achievements ({selectedYear})</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Streak</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sortedProfiles.map((profile, index) => {
                      const user = usersById.get(profile.userId);
                      const yearPoints = profile.yearlyPoints?.[selectedYear] || 0;
                      const yearAchievements = profile.achievementsByYear?.[selectedYear]?.length || 0;
                      const level = getUserLevel(yearPoints);
                      
                      return (
                        <tr key={profile.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                index === 0 ? 'bg-yellow-400 text-yellow-900' :
                                index === 1 ? 'bg-gray-300 text-gray-700' :
                                index === 2 ? 'bg-amber-600 text-amber-100' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {index + 1}
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{user?.displayName || 'Unknown User'}</div>
                                <div className="text-xs text-gray-500">{user?.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <span className="text-lg">{level.icon}</span>
                              <span className="text-sm text-gray-600">{level.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-amber-600">{yearPoints.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-gray-600">{(profile.totalPoints || 0).toLocaleString()}</span>
                              {!isReadOnly && (
                              <Tooltip text="Adjust Lifetime Points">
                                <button onClick={() => setAdjustLifetimeModal({ show: true, user: { ...profile, displayName: user?.displayName } })} className="px-1 py-0.5 text-xs text-indigo-600 hover:bg-indigo-50 rounded">±</button>
                              </Tooltip>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">{yearAchievements}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <span className="text-sm">🔥 {profile.currentStreak || 0}</span>
                              {!isReadOnly && (
                              <Tooltip text="Adjust Streak">
                                <button onClick={() => setAdjustStreakModal({ show: true, user: { ...profile, displayName: user?.displayName } })} className="px-1 py-0.5 text-xs text-orange-600 hover:bg-orange-50 rounded">±</button>
                              </Tooltip>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {!isReadOnly && (
                            <div className="flex items-center justify-end gap-1">
                              <Tooltip text="Adjust Yearly Points">
                                <button onClick={() => setAdjustPointsModal({ show: true, user: { ...profile, displayName: user?.displayName } })} className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded">± Pts</button>
                              </Tooltip>
                              <Tooltip text="Grant Achievements">
                                <button onClick={() => setGrantAchievementModal({ show: true, user: { ...profile, displayName: user?.displayName } })} className="px-2 py-1 text-xs text-green-600 hover:bg-green-50 rounded">+ Badges</button>
                              </Tooltip>
                              <Tooltip text="Generate Test Data">
                                <button onClick={() => setTestDataModal({ show: true, user: { ...profile, displayName: user?.displayName } })} className="px-2 py-1 text-xs text-purple-600 hover:bg-purple-50 rounded">🧪</button>
                              </Tooltip>
                              <Tooltip text="Reset Year Data">
                                <button onClick={() => setConfirmReset({ show: true, user: { ...profile, displayName: user?.displayName } })} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded">Reset</button>
                              </Tooltip>
                            </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {sortedProfiles.length === 0 && <div className="p-8 text-center text-gray-500">No users found</div>}
            </div>
          )}
          
          {activeTab === 'achievements' && (
            <div className="bg-white rounded-lg shadow">
              <div className="border-b px-4 py-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Achievements</h2>
                    <p className="text-sm text-gray-600 mt-1">Manage all achievements. Automatic achievements are triggered by user actions; manual achievements are granted by admins.</p>
                    {!achievementsSeeded && (
                      <div className="mt-2 flex items-center gap-2 text-amber-600 text-sm">
                        <span>⚠️ Achievements not yet seeded to Firestore.</span>
                        {!isReadOnly && (
                        <button 
                          onClick={handleSeedAchievements}
                          disabled={seeding}
                          className="px-2 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 disabled:opacity-50"
                        >
                          {seeding ? 'Seeding...' : 'Seed Now'}
                        </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={achievementFilter} onChange={(e) => setAchievementFilter(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500">
                      <option value="all">All ({achievements.length})</option>
                      <option value="automatic">Automatic ({achievements.filter(a => a.isAutomatic).length})</option>
                      <option value="manual">Manual ({achievements.filter(a => !a.isAutomatic).length})</option>
                      <option value="disabled">Disabled ({achievements.filter(a => a.isEnabled === false).length})</option>
                    </select>
                    {!isReadOnly && (
                    <button onClick={() => setAchievementModal({ show: true, achievement: null })} className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors whitespace-nowrap">+ New Achievement</button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                {filteredAchievements.length === 0 ? (
                  <div className="p-12 text-center border-2 border-dashed border-gray-200 rounded-lg">
                    <div className="text-4xl mb-2">🏆</div>
                    <div className="text-gray-600">No achievements match the current filter</div>
                    <div className="text-sm text-gray-500 mt-1">Try selecting a different filter or create a new achievement.</div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {['sales', 'tasks', 'engagement', 'consistency', 'quality', 'custom', 'other'].map(category => {
                      const categoryAchievements = achievementsByCategory[category];
                      if (!categoryAchievements || categoryAchievements.length === 0) return null;
                      
                      return (
                        <div key={category}>
                          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                            {category === 'sales' && '💰'}
                            {category === 'tasks' && '✅'}
                            {category === 'engagement' && '🤝'}
                            {category === 'consistency' && '🔥'}
                            {category === 'quality' && '⭐'}
                            {category === 'custom' && '✨'}
                            {category === 'other' && '📦'}
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                            <span className="text-xs font-normal text-gray-400">({categoryAchievements.length})</span>
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {categoryAchievements.map(achievement => (
                              <div key={achievement.id} className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${achievement.isEnabled === false ? 'bg-gray-50 opacity-60' : ''}`}>
                                <div className="flex items-start gap-3">
                                  <div className="text-4xl">{achievement.icon}</div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <div className="font-medium text-gray-900">{achievement.name}</div>
                                      {achievement.isAutomatic && <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-600 rounded">Auto</span>}
                                      {achievement.isEnabled === false && <span className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">Disabled</span>}
                                    </div>
                                    <div className="text-sm text-gray-600 mt-1">{achievement.description}</div>
                                    <div className="flex items-center gap-2 mt-2">
                                      <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">+{achievement.points} pts</span>
                                    </div>
                                    {!isReadOnly && (
                                    <div className="flex gap-2 mt-3">
                                      <button onClick={() => setAchievementModal({ show: true, achievement })} className="text-xs text-blue-600 hover:underline">Edit</button>
                                      <button onClick={async () => {
                                        try {
                                          await updateAchievement(currentUser?.uid, achievement.id, { isEnabled: !achievement.isEnabled });
                                          setToast({ message: `Achievement ${achievement.isEnabled ? 'disabled' : 'enabled'}`, type: 'success' });
                                          refreshData();
                                        } catch (err) { setToast({ message: err.message, type: 'error' }); }
                                      }} className="text-xs text-amber-600 hover:underline">{achievement.isEnabled !== false ? 'Disable' : 'Enable'}</button>
                                      {!achievement.isAutomatic && (
                                        <button onClick={async () => {
                                          if (!window.confirm('Delete this achievement? This cannot be undone.')) return;
                                          try {
                                            await deleteAchievement(currentUser?.uid, achievement.id);
                                            setToast({ message: 'Achievement deleted', type: 'success' });
                                            refreshData();
                                          } catch (err) { setToast({ message: err.message, type: 'error' }); }
                                        }} className="text-xs text-red-600 hover:underline">Delete</button>
                                      )}
                                    </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {activeTab === 'levels' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Level Configuration</h2>
                    <p className="text-sm text-gray-600 mt-1">Customize the levels, their point requirements, and display properties. Changes apply to all users.</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                  {mergedLevels.map((level) => {
                    const isModified = levelOverrides[level.level] !== undefined;
                    return (
                      <div key={level.level} className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${isModified ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="text-4xl">{level.icon}</div>
                          {isModified && <span className="px-1.5 py-0.5 text-xs bg-blue-200 text-blue-700 rounded">Modified</span>}
                        </div>
                        <div className="font-medium text-gray-900 text-lg">Level {level.level}</div>
                        <div className="text-sm text-gray-600 mt-1">{level.name}</div>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">{level.minPoints.toLocaleString()}+ pts</span>
                        </div>
                        <div className="flex gap-2 mt-3">
                          {!isReadOnly && <button onClick={() => setLevelModal({ show: true, level })} className="text-xs text-blue-600 hover:underline">Edit</button>}
                          {!isReadOnly && isModified && (
                            <button onClick={async () => {
                              try {
                                await resetLevelOverride(currentUser?.uid, level.level);
                                setToast({ message: `Level ${level.level} reset to default`, type: 'success' });
                                refreshData();
                              } catch (err) { setToast({ message: err.message, type: 'error' }); }
                            }} className="text-xs text-amber-600 hover:underline">Reset</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'scoring' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Point Values</h2>
                    <p className="text-sm text-gray-600 mt-1">Configure how many points users earn for different activities. Changes take effect immediately.</p>
                  </div>
                </div>
                
                {/* Group by category */}
                {['sales', 'engagement', 'tasks', 'quality', 'marketing', 'consistency', 'custom'].map(category => {
                  const categoryValues = pointValues.filter(pv => pv.category === category);
                  if (categoryValues.length === 0) return null;
                  
                  return (
                    <div key={category} className="mb-6">
                      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                        {category === 'sales' && '💰'}
                        {category === 'engagement' && '🤝'}
                        {category === 'tasks' && '✅'}
                        {category === 'quality' && '⭐'}
                        {category === 'marketing' && '📧'}
                        {category === 'consistency' && '🔥'}
                        {category === 'custom' && '🎯'}
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                        <span className="text-xs font-normal text-gray-400">({categoryValues.length})</span>
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {categoryValues.map(pv => (
                          <div key={pv.action} className={`border rounded-lg p-3 hover:shadow-sm transition-shadow ${pv.isModified ? 'bg-blue-50 border-blue-200' : pv.isCustom ? 'bg-purple-50 border-purple-200' : 'bg-white'}`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <div className="font-medium text-gray-900 text-sm">{pv.action.replace(/_/g, ' ')}</div>
                                  {pv.isModified && <span className="px-1.5 py-0.5 text-xs bg-blue-200 text-blue-700 rounded">Modified</span>}
                                  {pv.isCustom && <span className="px-1.5 py-0.5 text-xs bg-purple-200 text-purple-700 rounded">Custom</span>}
                                  {pv.isEnabled === false && <span className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">Disabled</span>}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">{pv.description}</div>
                                {pv.triggerHint && (
                                  <div className="text-xs text-indigo-600 mt-1 italic">💡 {pv.triggerHint}</div>
                                )}
                              </div>
                              <div className="text-right ml-2">
                                <div className="font-bold text-indigo-600">{pv.points} pts</div>
                                {pv.isModified && <div className="text-xs text-gray-400">default: {pv.defaultPoints}</div>}
                              </div>
                            </div>
                            {!isReadOnly && (
                            <div className="flex gap-2 mt-2">
                              <button onClick={() => setPointValueModal({ show: true, pointValue: pv })} className="text-xs text-blue-600 hover:underline">Edit</button>
                              {pv.isModified && (
                                <button onClick={async () => {
                                  try {
                                    await resetPointValue(currentUser?.uid, pv.action);
                                    setToast({ message: `Reset ${pv.action} to default`, type: 'success' });
                                    refreshData();
                                  } catch (err) { setToast({ message: err.message, type: 'error' }); }
                                }} className="text-xs text-amber-600 hover:underline">Reset</button>
                              )}
                              {pv.isCustom && (
                                <button onClick={async () => {
                                  if (!window.confirm(`Delete "${pv.action}"?`)) return;
                                  try {
                                    await deletePointValue(currentUser?.uid, pv.action);
                                    setToast({ message: `Deleted ${pv.action}`, type: 'success' });
                                    refreshData();
                                  } catch (err) { setToast({ message: err.message, type: 'error' }); }
                                }} className="text-xs text-red-600 hover:underline">Delete</button>
                              )}
                            </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {activeTab === 'log' && (
            <div className="space-y-4">
              {/* Filters and Controls */}
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-3">
                    {/* User Filter */}
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600">User:</label>
                      <select
                        value={logUserFilter}
                        onChange={(e) => setLogUserFilter(e.target.value)}
                        className={`border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 ${logUserFilter ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'}`}
                      >
                        <option value="">All Users</option>
                        {users.map(u => (
                          <option key={u.id} value={u.id}>{u.displayName || u.email}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Action Filter */}
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600">Action:</label>
                      <select
                        value={logActionFilter}
                        onChange={(e) => setLogActionFilter(e.target.value)}
                        className={`border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 ${logActionFilter ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'}`}
                      >
                        <option value="">All Actions</option>
                        {uniqueLogActions.map(action => (
                          <option key={action} value={action}>{action.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Clear Filters */}
                    {(logUserFilter || logActionFilter) && (
                      <button
                        onClick={() => { setLogUserFilter(''); setLogActionFilter(''); }}
                        className="text-sm text-indigo-600 hover:text-indigo-800"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="text-sm text-gray-600">
                      Showing {Math.min(filteredAndSortedLog.length, 100)} of {filteredAndSortedLog.length} entries
                    </div>
                    <button onClick={handleExportActivityLog} className="px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors">📥 Export</button>
                    {!isReadOnly && <button onClick={() => setTrimLogModal({ show: true })} className="px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors">🗑️ Trim</button>}
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th 
                          onClick={() => toggleLogSort('timestamp')}
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                        >
                          <div className="flex items-center gap-1">
                            Time
                            {logSortField === 'timestamp' && <span>{logSortDir === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th 
                          onClick={() => toggleLogSort('user')}
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                        >
                          <div className="flex items-center gap-1">
                            User
                            {logSortField === 'user' && <span>{logSortDir === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th 
                          onClick={() => toggleLogSort('action')}
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                        >
                          <div className="flex items-center gap-1">
                            Action
                            {logSortField === 'action' && <span>{logSortDir === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th 
                          onClick={() => toggleLogSort('points')}
                          className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                        >
                          <div className="flex items-center justify-end gap-1">
                            Points
                            {logSortField === 'points' && <span>{logSortDir === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredAndSortedLog.slice(0, 100).map(log => {
                        const user = usersById.get(log.userId);
                        const timestamp = log.timestamp?.toDate?.() || new Date();
                        
                        // Build comprehensive details display
                        const detailParts = [];
                        if (log.metadata?.reason) detailParts.push(log.metadata.reason);
                        if (log.metadata?.achievementName) detailParts.push(`Achievement: ${log.metadata.achievementName}`);
                        if (log.metadata?.achievementId && !log.metadata?.achievementName) detailParts.push(`Achievement ID: ${log.metadata.achievementId}`);
                        if (log.metadata?.deletedCount) detailParts.push(`Deleted ${log.metadata.deletedCount} entries`);
                        if (log.metadata?.entityId) detailParts.push(`Entity: ${log.metadata.entityId}`);
                        if (log.metadata?.entityType) detailParts.push(`Type: ${log.metadata.entityType}`);
                        if (log.metadata?.dealValue) detailParts.push(`Deal: $${log.metadata.dealValue.toLocaleString()}`);
                        if (log.metadata?.dealTier) detailParts.push(`Tier: ${log.metadata.dealTier}`);
                        if (log.metadata?.source && log.metadata.source !== 'manual') detailParts.push(`Source: ${log.metadata.source}`);
                        if (log.metadata?.oldStage && log.metadata?.newStage) detailParts.push(`Stage: ${log.metadata.oldStage} → ${log.metadata.newStage}`);
                        if (log.metadata?.year) detailParts.push(`Year: ${log.metadata.year}`);
                        if (log.metadata?.adminId) {
                          const admin = usersById.get(log.metadata.adminId);
                          detailParts.push(`By: ${admin?.displayName || 'Admin'}`);
                        }
                        
                        const detailsText = detailParts.length > 0 ? detailParts.join(' • ') : '—';
                        
                        return (
                          <tr key={log.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{timestamp.toLocaleDateString()} {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                            <td className="px-4 py-3"><div className="font-medium text-gray-900 text-sm">{user?.displayName || (log.userId === 'SYSTEM' ? 'System' : 'Unknown')}</div></td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs rounded-full ${log.action?.includes('ADMIN') ? 'bg-purple-100 text-purple-700' : log.action?.includes('BONUS') ? 'bg-green-100 text-green-700' : log.action?.includes('TRIM') ? 'bg-red-100 text-red-700' : log.action?.includes('WON') ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                                {(log.action || 'UNKNOWN').replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className={`px-4 py-3 text-right font-medium whitespace-nowrap ${log.points > 0 ? 'text-green-600' : log.points < 0 ? 'text-red-600' : 'text-gray-600'}`}>{log.points > 0 ? '+' : ''}{log.points}</td>
                            <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate" title={detailsText}>{detailsText}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {filteredAndSortedLog.length === 0 && <div className="p-8 text-center text-gray-500">{logUserFilter || logActionFilter ? 'No matching activities found' : 'No activity logged yet'}</div>}
              </div>
            </div>
          )}
          
          {activeTab === 'audit' && (
            <div className="space-y-4">
              {/* Summary Cards - Clickable to filter */}
              {blockedActivitySummary && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <button
                    onClick={() => setAuditFilter('pending')}
                    className={`bg-white rounded-lg shadow p-4 text-left transition-all hover:shadow-md ${auditFilter === 'pending' ? 'ring-2 ring-red-500' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="text-2xl">🚫</div>
                      <div>
                        <div className="text-2xl font-bold text-red-600">{blockedActivitySummary.pending || 0}</div>
                        <div className="text-sm text-gray-600">Pending Review</div>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => setAuditFilter('reviewed')}
                    className={`bg-white rounded-lg shadow p-4 text-left transition-all hover:shadow-md ${auditFilter === 'reviewed' ? 'ring-2 ring-green-500' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="text-2xl">✅</div>
                      <div>
                        <div className="text-2xl font-bold text-green-600">{blockedActivitySummary.reviewed || 0}</div>
                        <div className="text-sm text-gray-600">Reviewed</div>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => setAuditFilter('awarded')}
                    className={`bg-white rounded-lg shadow p-4 text-left transition-all hover:shadow-md ${auditFilter === 'awarded' ? 'ring-2 ring-amber-500' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="text-2xl">🎁</div>
                      <div>
                        <div className="text-2xl font-bold text-amber-600">{blockedActivitySummary.awarded || 0}</div>
                        <div className="text-sm text-gray-600">Manually Awarded</div>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => setAuditFilter('all')}
                    className={`bg-white rounded-lg shadow p-4 text-left transition-all hover:shadow-md ${auditFilter === 'all' ? 'ring-2 ring-blue-500' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="text-2xl">📊</div>
                      <div>
                        <div className="text-2xl font-bold text-blue-600">{blockedActivitySummary.totalBlocked || 0}</div>
                        <div className="text-sm text-gray-600">Total ({blockedActivitySummary.year})</div>
                      </div>
                    </div>
                  </button>
                </div>
              )}

              {/* Help Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="text-xl">💡</div>
                  <div className="text-sm text-blue-800">
                    <div className="font-medium mb-1">About the Anti-Abuse System</div>
                    <p className="text-blue-700">
                      The system silently logs activities that trigger anti-abuse rules (cooldowns, daily limits, backward stage changes, etc.) 
                      without blocking or notifying users. This preserves user experience while allowing admins to monitor for potential gaming of the system.
                      If an activity was legitimate, use "Award" to manually grant points. Use "Dismiss" to mark as reviewed without awarding.
                    </p>
                  </div>
                </div>
              </div>

              {/* Filter and Info */}
              <div className="bg-white rounded-lg shadow p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="font-medium text-gray-900">Blocked Activity Log</h3>
                  <p className="text-sm text-gray-600">Review activities that were silently blocked by the anti-abuse system. You can manually award points if the activity was legitimate.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select value={auditFilter} onChange={(e) => { setAuditFilter(e.target.value); setSelectedAuditLogs(new Set()); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500">
                    <option value="pending">Pending Review</option>
                    <option value="reviewed">Reviewed (Dismissed)</option>
                    <option value="awarded">Manually Awarded</option>
                    <option value="all">All</option>
                  </select>
                  <button onClick={loadAuditData} disabled={auditLoading} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm disabled:opacity-50">
                    {auditLoading ? '...' : '🔄'}
                  </button>
                  <button onClick={handleExportActivityAudit} className="px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-sm">
                    📥 Export
                  </button>
                </div>
              </div>
              
              {/* Bulk Actions Bar - Only show for pending filter or when items are selected */}
              {auditFilter === 'pending' && blockedActivityLog.filter(l => !l.reviewed && !l.awarded).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={selectedAuditLogs.size === blockedActivityLog.filter(l => !l.reviewed && !l.awarded).length && selectedAuditLogs.size > 0}
                          onChange={toggleSelectAllAuditLogs}
                          className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                        />
                        <span className="text-gray-700">Select All ({blockedActivityLog.filter(l => !l.reviewed && !l.awarded).length})</span>
                      </label>
                      {selectedAuditLogs.size > 0 && (
                        <span className="text-sm text-amber-700 font-medium">
                          {selectedAuditLogs.size} selected
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {selectedAuditLogs.size > 0 ? (
                        <>
                          {!isReadOnly && <button
                            onClick={handleBulkDismiss}
                            disabled={bulkActionLoading}
                            className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                          >
                            ✓ Dismiss Selected ({selectedAuditLogs.size})
                          </button>}
                          {!isReadOnly && <button
                            onClick={handleBulkAward}
                            disabled={bulkActionLoading}
                            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                          >
                            🎁 Award Selected ({selectedAuditLogs.size})
                          </button>}
                        </>
                      ) : (
                        <>
                          {!isReadOnly && <button
                            onClick={handleDismissAll}
                            disabled={bulkActionLoading}
                            className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                          >
                            ✓ Dismiss All
                          </button>}
                          {!isReadOnly && <button
                            onClick={handleAwardAll}
                            disabled={bulkActionLoading}
                            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                          >
                            🎁 Award All
                          </button>}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Blocked Activity Table */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                {auditLoading ? (
                  <div className="p-12 text-center text-gray-500">Loading audit data...</div>
                ) : blockedActivityLog.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="text-4xl mb-2">✅</div>
                    <div className="text-gray-600">
                      {auditFilter === 'pending' ? 'No pending activities to review' : 
                       auditFilter === 'awarded' ? 'No manually awarded activities' :
                       auditFilter === 'reviewed' ? 'No reviewed (dismissed) activities' :
                       'No blocked activities found'}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">The anti-abuse system hasn't flagged any recent activities.</div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          {auditFilter === 'pending' && (
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase w-10">
                              <span className="sr-only">Select</span>
                            </th>
                          )}
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Time</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">User</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Action</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Block Reason</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Would Award</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {blockedActivityLog.map(log => {
                          const user = usersById.get(log.userId);
                          const timestamp = log.timestamp?.toDate?.() || new Date();
                          const isPending = !log.awarded && !log.reviewed;
                          return (
                            <tr key={log.id} className={`hover:bg-gray-50 ${log.awarded ? 'bg-green-50' : log.reviewed ? 'bg-gray-50' : selectedAuditLogs.has(log.id) ? 'bg-indigo-50' : ''}`}>
                              {auditFilter === 'pending' && (
                                <td className="px-4 py-3 text-center">
                                  {isPending && (
                                    <input
                                      type="checkbox"
                                      checked={selectedAuditLogs.has(log.id)}
                                      onChange={() => toggleAuditLogSelection(log.id)}
                                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                                    />
                                  )}
                                </td>
                              )}
                              <td className="px-4 py-3 text-sm text-gray-600">{timestamp.toLocaleDateString()} {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900 text-sm">{user?.displayName || 'Unknown'}</div>
                                <div className="text-xs text-gray-500">{user?.email}</div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">{log.action?.replace(/_/g, ' ') || 'Unknown'}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  log.blockReason?.includes('cooldown') ? 'bg-yellow-100 text-yellow-700' :
                                  log.blockReason?.includes('limit') ? 'bg-orange-100 text-orange-700' :
                                  log.blockReason?.includes('changes') ? 'bg-purple-100 text-purple-700' :
                                  log.blockReason?.includes('stage') ? 'bg-red-100 text-red-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>{log.blockReason || 'Unknown'}</span>
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-amber-600">+{log.pointsWouldHaveAwarded || 0}</td>
                              <td className="px-4 py-3 text-center">
                                {log.awarded ? (
                                  <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Awarded</span>
                                ) : log.reviewed ? (
                                  <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-600">Reviewed</span>
                                ) : (
                                  <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700">Pending</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {!log.awarded && !log.reviewed && !isReadOnly && (
                                  <div className="flex items-center justify-end gap-1">
                                    <Tooltip text="Award Points Manually">
                                      <button onClick={() => handleAwardPoints(log)} className="px-2 py-1 text-xs text-green-600 hover:bg-green-50 rounded">🎁 Award</button>
                                    </Tooltip>
                                    <Tooltip text="Mark as Reviewed (No Action)">
                                      <button onClick={() => handleMarkReviewed(log.id)} className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded">✓ Dismiss</button>
                                    </Tooltip>
                                  </div>
                                )}
                                {log.awarded && (
                                  <span className="text-xs text-gray-500">+{log.awardedPoints} pts</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              
            </div>
          )}

          {/* Maintenance Tab */}
          {activeTab === 'maintenance' && (
            <div className="bg-white rounded-lg shadow p-6">
              <GamificationMaintenancePage embedded={true} />
            </div>
          )}
        </>
      )}
      
      {adjustPointsModal.show && <AdjustPointsModal user={adjustPointsModal.user} year={selectedYear} adminId={currentUser?.uid} onClose={() => setAdjustPointsModal({ show: false, user: null })} onSuccess={(msg) => { setAdjustPointsModal({ show: false, user: null }); setToast({ message: msg, type: 'success' }); refreshData(); }} />}
      {adjustStreakModal.show && <AdjustStreakModal user={adjustStreakModal.user} adminId={currentUser?.uid} onClose={() => setAdjustStreakModal({ show: false, user: null })} onSuccess={(msg) => { setAdjustStreakModal({ show: false, user: null }); setToast({ message: msg, type: 'success' }); refreshData(); }} />}
      {adjustLifetimeModal.show && <AdjustLifetimePointsModal user={adjustLifetimeModal.user} adminId={currentUser?.uid} onClose={() => setAdjustLifetimeModal({ show: false, user: null })} onSuccess={(msg) => { setAdjustLifetimeModal({ show: false, user: null }); setToast({ message: msg, type: 'success' }); refreshData(); }} />}
      {grantAchievementModal.show && <GrantAchievementModal user={grantAchievementModal.user} year={selectedYear} adminId={currentUser?.uid} achievements={achievements.filter(a => a.isEnabled !== false)} onClose={() => setGrantAchievementModal({ show: false, user: null })} onSuccess={(msg) => { setGrantAchievementModal({ show: false, user: null }); setToast({ message: msg, type: 'success' }); refreshData(); }} />}
      {confirmReset.show && <ConfirmResetModal user={confirmReset.user} year={selectedYear} adminId={currentUser?.uid} onClose={() => setConfirmReset({ show: false, user: null })} onSuccess={(msg) => { setConfirmReset({ show: false, user: null }); setToast({ message: msg, type: 'success' }); refreshData(); }} />}
      {testDataModal.show && <TestDataModal user={testDataModal.user} adminId={currentUser?.uid} onClose={() => setTestDataModal({ show: false, user: null })} onSuccess={(msg) => { setTestDataModal({ show: false, user: null }); setToast({ message: msg, type: 'success' }); refreshData(); }} />}
      {achievementModal.show && <AchievementModal achievement={achievementModal.achievement} adminId={currentUser?.uid} onClose={() => setAchievementModal({ show: false, achievement: null })} onSuccess={(msg) => { setAchievementModal({ show: false, achievement: null }); setToast({ message: msg, type: 'success' }); refreshData(); }} />}
      {trimLogModal.show && <TrimLogModal adminId={currentUser?.uid} onClose={() => setTrimLogModal({ show: false })} onSuccess={(msg) => { setTrimLogModal({ show: false }); setToast({ message: msg, type: 'success' }); refreshData(); }} />}
      {levelModal.show && <LevelModal level={levelModal.level} adminId={currentUser?.uid} onClose={() => setLevelModal({ show: false, level: null })} onSuccess={(msg) => { setLevelModal({ show: false, level: null }); setToast({ message: msg, type: 'success' }); refreshData(); }} />}
      {pointValueModal.show && <PointValueModal pointValue={pointValueModal.pointValue} adminId={currentUser?.uid} onClose={() => setPointValueModal({ show: false, pointValue: null })} onSuccess={(msg) => { setPointValueModal({ show: false, pointValue: null }); setToast({ message: msg, type: 'success' }); refreshData(); }} />}
    </main>
  );
};

// MODALS

const AdjustPointsModal = ({ user, year, adminId, onClose, onSuccess }) => {
  const [points, setPoints] = useState(0);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { setError('Please provide a reason'); return; }
    if (points === 0) { setError('Points adjustment cannot be 0'); return; }
    setSaving(true); setError('');
    try {
      await adminAdjustPoints(adminId, user.userId, points, reason, year);
      onSuccess(`Adjusted ${points > 0 ? '+' : ''}${points} points for ${user.displayName}`);
    } catch (err) { setError(err.message); setSaving(false); }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">± Adjust Points</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          <div className="mb-4 text-sm text-gray-600">Adjusting points for <strong>{user.displayName}</strong> ({year})</div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Points (negative to subtract)</label>
              <input type="number" value={points} onChange={(e) => setPoints(parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="e.g., 50 or -25" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" rows={2} placeholder="Reason for adjustment..." />
            </div>
            {error && <div className="text-red-600 text-sm">{error}</div>}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50">{saving ? 'Saving...' : 'Apply Adjustment'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const GrantAchievementModal = ({ user, year, adminId, achievements, onClose, onSuccess }) => {
  const [selectedAchievements, setSelectedAchievements] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  
  const availableAchievements = useMemo(() => {
    return achievements.filter(a => !user.achievements?.includes(a.id));
  }, [achievements, user.achievements]);
  
  const filteredAchievements = useMemo(() => {
    if (filter === 'all') return availableAchievements;
    if (filter === 'automatic') return availableAchievements.filter(a => a.isAutomatic);
    if (filter === 'manual') return availableAchievements.filter(a => !a.isAutomatic);
    return availableAchievements;
  }, [availableAchievements, filter]);
  
  const toggleAchievement = (id) => {
    setSelectedAchievements(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };
  
  const totalPoints = useMemo(() => availableAchievements.filter(a => selectedAchievements.has(a.id)).reduce((sum, a) => sum + (a.points || 0), 0), [availableAchievements, selectedAchievements]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedAchievements.size === 0) { setError('Please select at least one achievement'); return; }
    setSaving(true); setError('');
    try {
      const result = await adminGrantMultipleAchievements(adminId, user.userId, Array.from(selectedAchievements), year);
      onSuccess(`Granted ${result.granted} achievement(s) (+${result.totalPoints} pts) to ${user.displayName}`);
    } catch (err) { setError(err.message); setSaving(false); }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">🏅 Grant Achievements</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">Grant achievements to <strong>{user.displayName}</strong> for {year}</div>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="text-sm border border-gray-300 rounded-lg px-2 py-1">
              <option value="all">All ({availableAchievements.length})</option>
              <option value="automatic">Automatic ({availableAchievements.filter(a => a.isAutomatic).length})</option>
              <option value="manual">Manual ({availableAchievements.filter(a => !a.isAutomatic).length})</option>
            </select>
          </div>
        </div>
        
        {availableAchievements.length === 0 ? (
          <div className="p-6 text-center text-gray-500">User already has all available achievements!</div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="px-6 py-2 border-b bg-gray-50 flex items-center justify-between">
              <div className="text-sm text-gray-600">{selectedAchievements.size} selected • +{totalPoints.toLocaleString()} pts</div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setSelectedAchievements(new Set(filteredAchievements.map(a => a.id)))} className="text-xs text-indigo-600 hover:underline">Select All</button>
                <button type="button" onClick={() => setSelectedAchievements(new Set())} className="text-xs text-gray-600 hover:underline">Clear</button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {filteredAchievements.map(achievement => (
                <label key={achievement.id} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${selectedAchievements.has(achievement.id) ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="checkbox" checked={selectedAchievements.has(achievement.id)} onChange={() => toggleAchievement(achievement.id)} className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500" />
                  <div className="text-3xl">{achievement.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{achievement.name}</span>
                      {achievement.isAutomatic && <span className="px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-600">Auto</span>}
                    </div>
                    <div className="text-xs text-gray-500">{achievement.description}</div>
                  </div>
                  <div className="text-sm text-amber-600 font-medium">+{achievement.points}</div>
                </label>
              ))}
            </div>
            
            <div className="p-4 border-t bg-gray-50">
              {error && <div className="text-red-600 text-sm mb-3">{error}</div>}
              <div className="flex gap-3">
                <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving || selectedAchievements.size === 0} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50">{saving ? 'Granting...' : `Grant ${selectedAchievements.size} Achievement${selectedAchievements.size !== 1 ? 's' : ''}`}</button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const ConfirmResetModal = ({ user, year, adminId, onClose, onSuccess }) => {
  const [confirmText, setConfirmText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (confirmText !== 'RESET') { setError('Please type RESET to confirm'); return; }
    setSaving(true); setError('');
    try {
      await adminResetYearlyData(adminId, user.userId, year);
      onSuccess(`Reset ${year} data for ${user.displayName}`);
    } catch (err) { setError(err.message); setSaving(false); }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-red-600">⚠️ Reset Year Data</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <strong>Warning:</strong> This will reset ALL {year} data for {user.displayName}:
            <ul className="list-disc ml-5 mt-2"><li>Points for {year} will be set to 0</li><li>Achievements earned in {year} will be cleared</li><li>Stats for {year} will be reset</li></ul>
            <p className="mt-2">This action cannot be undone!</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type RESET to confirm</label>
              <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" placeholder="RESET" />
            </div>
            {error && <div className="text-red-600 text-sm">{error}</div>}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving || confirmText !== 'RESET'} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50">{saving ? 'Resetting...' : 'Reset Data'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const TestDataModal = ({ user, adminId, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleGenerateData = async () => {
    setLoading(true); setError('');
    try {
      const result = await generateTestGamificationData(adminId, user.userId, user.displayName);
      onSuccess(`Generated test data for ${user.displayName}: ${result.data.totalPoints} points, ${result.data.achievements} achievements, ${result.data.streak}-day streak`);
    } catch (err) { setError(err.message); setLoading(false); }
  };
  
  const handleClearData = async () => {
    if (!window.confirm(`Clear ALL gamification data for ${user.displayName}? This cannot be undone.`)) return;
    setLoading(true); setError('');
    try {
      await clearGamificationData(adminId, user.userId);
      onSuccess(`Cleared all gamification data for ${user.displayName}`);
    } catch (err) { setError(err.message); setLoading(false); }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">🧪 Test Data Generator</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          <div className="mb-4 text-sm text-gray-600">Managing test data for <strong>{user.displayName}</strong></div>
          <div className="space-y-3 mb-4">
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="font-medium text-purple-800 mb-1">🎲 Generate Random Test Data</div>
              <div className="text-sm text-purple-600">Creates realistic gamification data including random points, achievements based on stats, and streak data.</div>
            </div>
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="font-medium text-red-800 mb-1">🗑️ Clear All Data</div>
              <div className="text-sm text-red-600">Removes all gamification data for this user. Cannot be undone.</div>
            </div>
          </div>
          {error && <div className="text-red-600 text-sm mb-4">{error}</div>}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="button" onClick={handleClearData} disabled={loading} className="px-4 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 disabled:opacity-50">Clear</button>
            <button type="button" onClick={handleGenerateData} disabled={loading} className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50">{loading ? 'Processing...' : 'Generate Data'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EMOJI_OPTIONS = ['🏆', '🎯', '⭐', '💎', '🔥', '✨', '🎖️', '🌟', '💪', '🚀', '💡', '🎪', '🏅', '👑', '💰', '📈', '🎉', '🌈', '⚡', '🔮', '✅', '📝', '🤝', '⏰', '💬'];
const CATEGORY_OPTIONS = ['sales', 'tasks', 'engagement', 'consistency', 'quality', 'custom', 'other'];

const AchievementModal = ({ achievement, adminId, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    name: achievement?.name || '',
    description: achievement?.description || '',
    icon: achievement?.icon || '🎯',
    category: achievement?.category || 'custom',
    points: achievement?.points || 100,
    isEnabled: achievement?.isEnabled !== false,
    isAutomatic: achievement?.isAutomatic || false
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.description.trim()) { setError('Description is required'); return; }
    setSaving(true); setError('');
    try {
      if (achievement) {
        await updateAchievement(adminId, achievement.id, form);
        onSuccess(`Updated achievement "${form.name}"`);
      } else {
        await createAchievement(adminId, form);
        onSuccess(`Created new achievement "${form.name}"`);
      }
    } catch (err) { setError(err.message); setSaving(false); }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">{achievement ? '✏️ Edit Achievement' : '✨ New Achievement'}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          
          {achievement?.isAutomatic && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              <strong>Note:</strong> This is an automatic achievement. Editing it will change how it's displayed but the trigger logic remains unchanged.
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
              <div className="flex flex-wrap gap-2">
                {EMOJI_OPTIONS.map(emoji => (
                  <button key={emoji} type="button" onClick={() => setForm(f => ({ ...f, icon: emoji }))} className={`w-10 h-10 text-2xl rounded-lg border-2 transition-colors ${form.icon === emoji ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}>{emoji}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="e.g., Super Seller" maxLength={50} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" rows={2} placeholder="e.g., Close 5 deals in a single week" maxLength={200} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Points</label>
                <input type="number" value={form.points} onChange={(e) => setForm(f => ({ ...f, points: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" min={0} max={10000} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
                  {CATEGORY_OPTIONS.map(cat => (<option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>))}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <input type="checkbox" id="isEnabled" checked={form.isEnabled} onChange={(e) => setForm(f => ({ ...f, isEnabled: e.target.checked }))} className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500" />
                <label htmlFor="isEnabled" className="text-sm text-gray-700">Enabled (available for granting)</label>
              </div>
              {!achievement && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                  <strong>💡 Tip:</strong> Custom achievements are manually granted by admins to recognize special accomplishments, milestones, or contributions that aren't tracked automatically.
                </div>
              )}
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border">
              <div className="text-xs text-gray-500 mb-2">Preview</div>
              <div className="flex items-center gap-3">
                <div className="text-4xl">{form.icon}</div>
                <div>
                  <div className="font-medium text-gray-900">{form.name || 'Achievement Name'}</div>
                  <div className="text-sm text-gray-600">{form.description || 'Achievement description'}</div>
                  <div className="flex gap-2 mt-1">
                    <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">+{form.points} pts</span>
                    {form.isAutomatic && <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-600">Auto</span>}
                    <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">{form.category}</span>
                  </div>
                </div>
              </div>
            </div>
            {error && <div className="text-red-600 text-sm">{error}</div>}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50">{saving ? 'Saving...' : achievement ? 'Update Achievement' : 'Create Achievement'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const LEVEL_EMOJI_OPTIONS = ['🌱', '🌿', '🌳', '⭐', '🌟', '💫', '🔥', '💎', '👑', '🏆', '🎯', '🚀', '⚡', '🦁', '🦅', '🐉', '🎖️', '🏅', '🥇', '✨'];

const LevelModal = ({ level, adminId, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    icon: level?.icon || '⭐',
    name: level?.name || '',
    minPoints: level?.minPoints || 0
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const defaultLevel = LEVELS.find(l => l.level === level?.level);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Please provide a level name'); return; }
    if (form.minPoints < 0) { setError('Points must be 0 or greater'); return; }
    setSaving(true); setError('');
    try {
      await updateLevelOverride(adminId, level.level, {
        icon: form.icon,
        name: form.name.trim(),
        minPoints: form.minPoints
      });
      onSuccess(`Level ${level.level} updated successfully`);
    } catch (err) { setError(err.message); setSaving(false); }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">📊 Edit Level {level?.level}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          
          {defaultLevel && (
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
              <strong>Default:</strong> {defaultLevel.icon} {defaultLevel.name} • {defaultLevel.minPoints.toLocaleString()}+ pts
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
              <div className="flex flex-wrap gap-2">
                {LEVEL_EMOJI_OPTIONS.map(emoji => (
                  <button key={emoji} type="button" onClick={() => setForm(f => ({ ...f, icon: emoji }))} className={`w-10 h-10 text-2xl rounded-lg border-2 transition-colors ${form.icon === emoji ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}>{emoji}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Level Name *</label>
              <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="e.g., Rookie, Pro, Legend" maxLength={30} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Points Required</label>
              <input type="number" value={form.minPoints} onChange={(e) => setForm(f => ({ ...f, minPoints: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" min={0} max={1000000} />
              <div className="text-xs text-gray-500 mt-1">Users need this many points to reach this level</div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border">
              <div className="text-xs text-gray-500 mb-2">Preview</div>
              <div className="flex items-center gap-3">
                <div className="text-4xl">{form.icon}</div>
                <div>
                  <div className="font-medium text-gray-900 text-lg">Level {level?.level}</div>
                  <div className="text-sm text-gray-600">{form.name || 'Level Name'}</div>
                  <div className="flex gap-2 mt-1">
                    <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">{form.minPoints.toLocaleString()}+ pts</span>
                  </div>
                </div>
              </div>
            </div>
            {error && <div className="text-red-600 text-sm">{error}</div>}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50">{saving ? 'Saving...' : 'Save Level'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const POINT_CATEGORIES = ['sales', 'engagement', 'tasks', 'quality', 'marketing', 'consistency', 'custom'];

const PointValueModal = ({ pointValue, adminId, onClose, onSuccess }) => {
  const isNew = !pointValue;
  const [form, setForm] = useState({
    action: pointValue?.action || '',
    points: pointValue?.points || 10,
    category: pointValue?.category || 'custom',
    description: pointValue?.description || '',
    triggerHint: pointValue?.triggerHint || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const defaultValue = DEFAULT_POINT_VALUES[pointValue?.action];
  const isCustom = pointValue?.isCustom || !defaultValue;
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isNew && !form.action.trim()) { setError('Action name is required'); return; }
    if (form.points < 0) { setError('Points must be 0 or greater'); return; }
    setSaving(true); setError('');
    try {
      if (isNew) {
        await createPointValue(adminId, {
          action: form.action,
          points: form.points,
          category: form.category,
          description: form.description.trim(),
          triggerHint: form.triggerHint.trim()
        });
        onSuccess(`Created new point trigger "${form.action}"`);
      } else {
        await updatePointValue(adminId, pointValue.action, {
          points: form.points,
          category: form.category,
          description: form.description.trim() || pointValue.description,
          triggerHint: form.triggerHint.trim()
        });
        onSuccess(`Updated ${pointValue.action.replace(/_/g, ' ')}`);
      }
    } catch (err) { setError(err.message); setSaving(false); }
  };
  
  const handleDelete = async () => {
    if (!window.confirm(`Delete "${pointValue.action}"? This cannot be undone.`)) return;
    setSaving(true); setError('');
    try {
      await deletePointValue(adminId, pointValue.action);
      onSuccess(`Deleted ${pointValue.action}`);
    } catch (err) { setError(err.message); setSaving(false); }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">{isNew ? '➕ New Point Trigger' : '⚡ Edit Point Value'}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          
          {!isNew && (
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="font-medium text-gray-900">{pointValue?.action.replace(/_/g, ' ')}</div>
              <div className="text-sm text-gray-600 mt-1">{pointValue?.description}</div>
              {defaultValue && (
                <div className="text-xs text-gray-500 mt-2">Default: {typeof defaultValue === 'object' ? defaultValue.points : defaultValue} pts</div>
              )}
              {isCustom && <span className="inline-block mt-2 px-2 py-0.5 text-xs bg-purple-100 text-purple-600 rounded-full">Custom</span>}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {isNew && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action Name *</label>
                <input type="text" value={form.action} onChange={(e) => setForm(f => ({ ...f, action: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono" placeholder="CUSTOM_ACTION_NAME" maxLength={50} />
                <div className="text-xs text-gray-500 mt-1">Use SCREAMING_SNAKE_CASE (e.g., ORDER_PLACED)</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Points Awarded</label>
                <input type="number" value={form.points} onChange={(e) => setForm(f => ({ ...f, points: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" min={0} max={10000} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
                  {POINT_CATEGORIES.map(cat => (<option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input type="text" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="Brief description of this activity" maxLength={100} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trigger Implementation Hint</label>
              <textarea value={form.triggerHint} onChange={(e) => setForm(f => ({ ...f, triggerHint: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm" placeholder="Where/how is this triggered in the app? e.g., 'Triggered via CustomerModal when saving'" rows={2} maxLength={200} />
              <div className="text-xs text-gray-500 mt-1">Document how this point trigger is called in the code</div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border">
              <div className="text-xs text-gray-500 mb-2">Preview</div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">{(isNew ? form.action : pointValue?.action).replace(/_/g, ' ') || 'ACTION NAME'}</div>
                  <div className="text-xs text-gray-500">{form.category}</div>
                </div>
                <div className="font-bold text-indigo-600 text-lg">{form.points} pts</div>
              </div>
            </div>
            {error && <div className="text-red-600 text-sm">{error}</div>}
            <div className="flex gap-3 pt-2">
              {!isNew && isCustom && (
                <button type="button" onClick={handleDelete} disabled={saving} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50">Delete</button>
              )}
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50">{saving ? 'Saving...' : isNew ? 'Create' : 'Save'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const TrimLogModal = ({ adminId, onClose, onSuccess }) => {
  const [daysToKeep, setDaysToKeep] = useState(90);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmText, setConfirmText] = useState('');
  
  const handleTrim = async () => {
    if (confirmText !== 'TRIM') { setError('Please type TRIM to confirm'); return; }
    setLoading(true); setError('');
    try {
      let totalDeleted = 0;
      let hasMore = true;
      let iterations = 0;
      const maxIterations = 10; // Safety limit
      
      while (hasMore && iterations < maxIterations) {
        const result = await trimActivityLog(adminId, daysToKeep);
        totalDeleted += result.deleted;
        hasMore = result.hasMore;
        iterations++;
        if (hasMore) await new Promise(r => setTimeout(r, 500));
      }
      
      if (totalDeleted === 0) {
        onSuccess(`No log entries older than ${daysToKeep} days found`);
      } else {
        onSuccess(`Deleted ${totalDeleted} log entries older than ${daysToKeep} days`);
      }
    } catch (err) { setError(err.message); setLoading(false); }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-red-600">🗑️ Trim Activity Log</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <strong>Warning:</strong> This will permanently delete activity log entries older than the specified number of days. This action cannot be undone.
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Keep entries from the last</label>
              <div className="flex items-center gap-2">
                <input type="number" value={daysToKeep} onChange={(e) => setDaysToKeep(parseInt(e.target.value) || 30)} className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" min={7} max={365} />
                <span className="text-gray-600">days</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">Entries older than {daysToKeep} days will be deleted (based on date only, not time)</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type TRIM to confirm</label>
              <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" placeholder="TRIM" />
            </div>
            {error && <div className="text-red-600 text-sm">{error}</div>}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="button" onClick={handleTrim} disabled={loading || confirmText !== 'TRIM'} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50">{loading ? 'Deleting...' : 'Trim Log'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdjustStreakModal = ({ user, adminId, onClose, onSuccess }) => {
  const [streakChange, setStreakChange] = useState(0);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { setError('Please provide a reason'); return; }
    if (streakChange === 0) { setError('Streak change cannot be 0'); return; }
    setSaving(true); setError('');
    try {
      const result = await adminAdjustStreak(adminId, user.userId, streakChange, reason);
      onSuccess(`Adjusted streak for ${user.displayName}: ${result.oldStreak} → ${result.newStreak}`);
    } catch (err) { setError(err.message); setSaving(false); }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">🔥 Adjust Streak</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          <div className="mb-4 text-sm text-gray-600">
            Adjusting streak for <strong>{user.displayName}</strong>
            <div className="mt-1 text-orange-600">Current streak: 🔥 {user.currentStreak || 0}</div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Streak change (negative to subtract)</label>
              <input type="number" value={streakChange} onChange={(e) => setStreakChange(parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500" placeholder="e.g., 5 or -3" />
              <div className="text-xs text-gray-500 mt-1">New streak will be: 🔥 {Math.max(0, (user.currentStreak || 0) + streakChange)}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500" rows={2} placeholder="Reason for adjustment..." />
            </div>
            {error && <div className="text-red-600 text-sm">{error}</div>}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 disabled:opacity-50">{saving ? 'Saving...' : 'Apply Adjustment'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const AdjustLifetimePointsModal = ({ user, adminId, onClose, onSuccess }) => {
  const [points, setPoints] = useState(0);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { setError('Please provide a reason'); return; }
    if (points === 0) { setError('Points adjustment cannot be 0'); return; }
    setSaving(true); setError('');
    try {
      await adminAdjustLifetimePoints(adminId, user.userId, points, reason);
      onSuccess(`Adjusted ${points > 0 ? '+' : ''}${points} lifetime points for ${user.displayName}`);
    } catch (err) { setError(err.message); setSaving(false); }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">💎 Adjust Lifetime Points</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          <div className="mb-4 text-sm text-gray-600">
            Adjusting lifetime points for <strong>{user.displayName}</strong>
            <div className="mt-1 text-indigo-600">Current lifetime: {(user.totalPoints || 0).toLocaleString()} pts</div>
          </div>
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            <strong>Note:</strong> This adjusts only the total lifetime points, not the yearly points. Use "± Pts" button to adjust yearly points.
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Points change (negative to subtract)</label>
              <input type="number" value={points} onChange={(e) => setPoints(parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="e.g., 500 or -100" />
              <div className="text-xs text-gray-500 mt-1">New lifetime total: {((user.totalPoints || 0) + points).toLocaleString()} pts</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" rows={2} placeholder="Reason for adjustment..." />
            </div>
            {error && <div className="text-red-600 text-sm">{error}</div>}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50">{saving ? 'Saving...' : 'Apply Adjustment'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AchievementManagementPage;